import { useMemo } from 'react'
import sanitizeHtml from 'sanitize-html'
import { PolicyDetails } from '@/Reservations/Manage/PolicyDetails'
import type { Fee } from '@sevenrooms/domain'
import { FormatService, useLocales } from '@sevenrooms/locales'
import { HTMLContent } from '@sevenrooms/ui-kit/core'
import { VStack, CardSection, Box } from '@sevenrooms/ui-kit/layout'
import { Text } from '@sevenrooms/ui-kit/typography'
import { reservationWidgetMessages } from '../../reservationWidgetMessages'
import { formatFee } from '../../utils'
import { SummaryRow } from '../SummaryRow'

export interface PrivateEventsSummaryProps {
  fees?: Fee[]
  minSpend?: number
  currencyCode: string
  showPrivateEventsPolicy: boolean
  bookingCheckoutPolicy?: string
  cancellationPolicy?: string
}

export function PrivateEventsSummary({
  fees,
  minSpend,
  currencyCode,
  showPrivateEventsPolicy,
  bookingCheckoutPolicy,
  cancellationPolicy,
}: PrivateEventsSummaryProps) {
  const { formatMessage } = useLocales()
  const privateEventsPolicy = formatMessage(reservationWidgetMessages.policyPrivateEventsBody)
  const hasPrivateEventsPolicy = useMemo(
    () => (privateEventsPolicy ? !!sanitizeHtml(privateEventsPolicy, { allowedTags: [] }).trim() : false),
    [privateEventsPolicy]
  )

  if (
    minSpend === undefined &&
    !fees?.length &&
    (!hasPrivateEventsPolicy || (!showPrivateEventsPolicy && !bookingCheckoutPolicy && !cancellationPolicy))
  ) {
    return null
  }

  return (
    <CardSection p="lm" data-test="private-events-summary" borderColor="borders" backgroundColor="primaryBackground" borderRadius="s">
      <VStack spacing="lm">
        {showPrivateEventsPolicy && <Text textStyle="h3">{formatMessage(reservationWidgetMessages.policyPrivateEventsLabel)}</Text>}
        {(minSpend !== undefined || !!fees?.length) && (
          <VStack spacing="sm">
            {minSpend !== undefined && (
              <>
                <Text textStyle="body1Bold">
                  {formatMessage(reservationWidgetMessages.resWidgetPrivateEventsGroupBookingSummarySubLabel)}
                </Text>
                <SummaryRow
                  data-test="min-spend-value"
                  primaryText={formatMessage(reservationWidgetMessages.resWidgetPrivateEventsGroupBookingSummaryMinSpendLabel)}
                  secondaryText={FormatService.formatCurrency(minSpend, currencyCode)}
                />
              </>
            )}
            {fees?.map(fee => (
              <SummaryRow
                primaryText={fee.name ?? ''}
                key={fee.id}
                data-test={`${fee.id}-fee`}
                secondaryText={formatFee(fee, currencyCode)}
              />
            ))}
          </VStack>
        )}
        {showPrivateEventsPolicy && hasPrivateEventsPolicy && privateEventsPolicy && (
          <Box>
            <Text data-test="private-events-policy">
              <HTMLContent content={privateEventsPolicy} allowedTags={false} />
            </Text>
          </Box>
        )}
        {!showPrivateEventsPolicy && (
          <PolicyDetails
            dataTestId="reservation-widget-policy-details"
            cancellationPolicy={cancellationPolicy}
            bookingCheckoutPolicy={bookingCheckoutPolicy}
            showSpecialAttentionMessage={false}
            showBookingCheckoutPolicyHeader={false}
          />
        )}
      </VStack>
    </CardSection>
  )
}
